import { useEffect } from 'react';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { findPreferredStatus } from '../FlowchartView/utils/statusUtils';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';

/**
 * When a decision node is locked (unlock conditions not met), any stored choice
 * is invalid — clear answer/selectedOption and reset status like an unanswered decision.
 */
export function useClearLockedDecisionAnswers({
  localSprint,
  setLocalSprint,
  nestedSprints = [],
}) {
  const tasks = localSprint?.tasks;
  const statuses = localSprint?.statuses || DEFAULT_STATUSES;
  const sprintId = localSprint?.id;

  useEffect(() => {
    if (!tasks?.length) return;

    const lockStates = calculateLockStates(tasks, nestedSprints, statuses);
    const preferredNotStarted = findPreferredStatus(statuses, 'not_started');
    const notStartedStatusId = preferredNotStarted?.id || 'pending';

    const idsToClear = tasks
      .filter(
        (t) =>
          t.nodeType === 'decision' &&
          lockStates[t.id]?.isLocked &&
          (t.answer != null || t.selectedOption != null)
      )
      .map((t) => t.id);

    if (idsToClear.length === 0) return;

    const idSet = new Set(idsToClear);

    setLocalSprint((prev) => {
      if (!prev) return prev;
      let changed = false;
      const nextTasks = prev.tasks.map((t) => {
        if (!idSet.has(t.id)) return t;
        if (t.answer == null && (t.selectedOption == null || t.selectedOption === undefined)) {
          return t;
        }
        changed = true;
        return {
          ...t,
          answer: null,
          selectedOption: undefined,
          status: notStartedStatusId,
          completedAt: null,
          startedAt: null,
        };
      });
      if (!changed) return prev;
      return { ...prev, tasks: nextTasks };
    });
  }, [sprintId, tasks, statuses, nestedSprints, setLocalSprint]);
}

export default useClearLockedDecisionAnswers;
